import { useState } from "react";
import { CalendarDays, Clock, TrendingUp, AlertTriangle, DoorOpen, DoorClosed } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useOperationalPeriod, OperationalPeriod } from "@/hooks/useOperationalPeriod";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { ClosePeriodDialog } from "./ClosePeriodDialog";

interface PeriodStatusCardProps {
  hasOpenCashRegister: boolean;
  onPeriodOpened?: () => void;
}

export function PeriodStatusCard({ hasOpenCashRegister, onPeriodOpened }: PeriodStatusCardProps) {
  const { currentPeriod, isPeriodOpen, openPeriod, closePeriod, isLoading } = useOperationalPeriod();
  const { activeRestaurant } = useRestaurantContext();
  const showTotalsOnClose = (activeRestaurant as any)?.show_period_totals_on_close ?? true;
  const [closePeriodDialogOpen, setClosePeriodDialogOpen] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleOpenPeriod = async () => {
    setIsOpening(true);
    const result = await openPeriod();
    setIsOpening(false);
    if (result && onPeriodOpened) {
      onPeriodOpened();
    }
  };

  const handleClosePeriod = async (notes?: string) => {
    await closePeriod(notes);
    setClosePeriodDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isPeriodOpen) {
    return (
      <Card className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <div>
                <CardTitle className="text-lg">Período Fechado</CardTitle>
                <CardDescription>
                  Abra um novo período para realizar vendas
                </CardDescription>
              </div>
            </div>
            <Button 
              onClick={handleOpenPeriod} 
              disabled={isOpening}
              className="gap-2"
            >
              <DoorOpen className="h-4 w-4" />
              {isOpening ? "Abrindo..." : "Abrir Período"}
            </Button>
          </div>
        </CardHeader>
      </Card>
    );
  }

  const periodDuration = formatDistanceToNow(new Date(currentPeriod!.opened_at), { locale: ptBR });

  return (
    <>
      <Card className="border-primary/50 bg-primary/5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-6 w-6 text-primary" />
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">
                    Período: {format(new Date(currentPeriod!.reference_date + "T12:00:00"), "dd/MM/yyyy")}
                  </CardTitle>
                  <Badge variant="default" className="bg-green-600">Aberto</Badge>
                </div>
                <CardDescription className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Aberto há {periodDuration}
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setClosePeriodDialogOpen(true)}
              disabled={hasOpenCashRegister}
              title={hasOpenCashRegister ? "Feche o caixa antes de fechar o período" : undefined}
            >
              <DoorClosed className="h-4 w-4" />
              Fechar Período
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-background border">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                Vendas do Período
              </p>
              <p className="text-xl font-bold">{formatCurrency(currentPeriod!.total_sales)}</p>
            </div>
            <div className="p-3 rounded-lg bg-background border">
              <p className="text-sm text-muted-foreground">Pedidos</p>
              <p className="text-xl font-bold">{currentPeriod!.total_orders}</p>
            </div>
          </div>
          {hasOpenCashRegister && (
            <p className="text-xs text-muted-foreground mt-3">
              * Feche o turno de caixa antes de fechar o período
            </p>
          )}
        </CardContent>
      </Card>

      <ClosePeriodDialog
        open={closePeriodDialogOpen}
        onOpenChange={setClosePeriodDialogOpen}
        onConfirm={handleClosePeriod}
        period={currentPeriod!}
        showTotals={showTotalsOnClose}
      />
    </>
  );
}
